import React, { useEffect, useState, useCallback } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToaster } from '../components/common/Toaster';
import {
  Card,
  CardContent,
  CardIconHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/table';
import { cn } from '../lib/utils';
import { CheckSquare, Calendar } from 'lucide-react';
import { taskService } from '../Services/taskService';
import PriorityBadge from '../components/Tasks/PriorityBadge';
import StatusBadge from '../components/Tasks/StatusBadge';
import TaskDetailSheet from '../components/Tasks/TaskDetailSheet';
import type { TaskItemDto, TaskStatus } from '../types/tasks';

type FilterTab = 'All' | TaskStatus;

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'Todo', label: 'To Do' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'InReview', label: 'In Review' },
  { value: 'Done', label: 'Done' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const MyTasks: React.FC = () => {
  const { notify } = useToaster();
  const [tasks, setTasks] = useState<TaskItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('All');

  const [selectedTask, setSelectedTask] = useState<TaskItemDto | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = activeFilter === 'All' ? undefined : activeFilter;
      const data = await taskService.getMyTasks(statusParam);
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleTaskClick = (task: TaskItemDto) => {
    setSelectedTask(task);
    setDetailSheetOpen(true);
  };

  const handleTaskUpdated = (updatedTask: TaskItemDto) => {
    setTasks((prev) => prev.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
    notify('Saved', 'Task updated successfully', 'success');
  };

  const handleTaskDeleted = (taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    notify('Deleted', 'Task deleted successfully', 'success');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <Card>
        <CardIconHeader icon={<CheckSquare size={22} className="text-primary" />}>
          <CardTitle>My Tasks</CardTitle>
          <CardDescription>
            View and manage all tasks assigned to you across projects.
          </CardDescription>
        </CardIconHeader>
      </Card>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveFilter(tab.value)}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all duration-200",
              activeFilter === tab.value
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tasks Table */}
      {loading ? (
        <LoadingSpinner label="Loading tasks..." />
      ) : tasks.length === 0 ? (
        <Card>
          <CardContent>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg py-12">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="rounded-full bg-muted p-3">
                  <CheckSquare size={24} className="text-muted-foreground" />
                </div>
                <p className="font-medium text-muted-foreground">No tasks found</p>
                <p className="text-sm text-muted-foreground/75">
                  {activeFilter === 'All'
                    ? 'You have no tasks assigned to you yet.'
                    : `No tasks with status "${FILTER_TABS.find(t => t.value === activeFilter)?.label}".`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow
                    key={task.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleTaskClick(task)}
                  >
                    <TableCell className="font-medium max-w-[300px]">
                      <span className="line-clamp-1">{task.title}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground text-sm">
                        {task.projectName}
                      </span>
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={task.priority} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={task.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        {task.dueDate && <Calendar className="h-3.5 w-3.5" />}
                        {formatDate(task.dueDate)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <TaskDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        task={selectedTask}
        members={[]}
        onTaskUpdated={handleTaskUpdated}
        onTaskDeleted={handleTaskDeleted}
      />
    </div>
  );
};

export default MyTasks;
